import { Box, CloseButton, Icon, IconButton, Paragraph, Spinner, Text } from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';
import { useState } from 'react';
import ReactImagesViewer from 'react-images-viewer';
import useModal from '../../hooks/useModal';
import conversionUtils from '../../utils/conversionUtils';
import stringUtils from '../../utils/stringUtils';
import type { AttachmentsForMessage } from '../MessageAttachments';
import {
  emitIrenePreviewAttachmentsEvent,
  IRENE_PREVIEW_ATTACHMENTS_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/attachment';
import { useAuth } from '@zg-rentals/auth-comet';

export type Props = {
  imageAttachments: AttachmentsForMessage;
  selectedImageIndex: number;
};

const ImagesViewer = ({ imageAttachments, selectedImageIndex }: Props) => {
  const { user } = useAuth();
  const { clearModal } = useModal();
  const imageIndex = selectedImageIndex === -1 ? 0 : selectedImageIndex;
  const [currentImageIndex, setCurrentImageIndex] = useState(imageIndex || 0);

  const images = imageAttachments.map(({ fileName, fileSizeBytes, previewUrl }) => {
    const isZDocPreviewUrl = previewUrl?.includes('zillowdocs');
    // zDocs API is capable of resizing images with height and width params
    // as opposed to images generated on the FE during upload
    const src = isZDocPreviewUrl ? `${previewUrl}&width=1024` : previewUrl;

    return {
      src,
      fileName,
      fileSizeBytes,
    };
  });

  const handleNext = () => {
    const nextIndex = currentImageIndex + 1;
    setCurrentImageIndex(nextIndex);
  };

  const handlePrevious = () => {
    const prevIndex = currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
  };

  const handleClose = () => clearModal();

  const handleEventTracking = () => {
    emitIrenePreviewAttachmentsEvent(user);
  };

  const currentImage = images[currentImageIndex];

  if (!images.length) return null;

  const customControls = [
    <Box
      css={{
        display: 'flex',
        flexDirection: 'row',
        position: 'absolute',
        top: ' -60px',
        width: '100%',
        alignItems: 'start',
        color: 'white',
      }}
      key="header"
    >
      <CloseButton
        onClick={handleClose}
        onImpact={true}
        css={{ '& svg': { height: '1.5em', width: '1.5em' }, marginTop: 'tighter' }}
      />

      {currentImage && (
        <>
          {currentImage.fileName && currentImage.fileSizeBytes && (
            <Box css={{ display: 'flex', flexDirection: 'column', marginInline: 'looser' }}>
              <Paragraph textStyle="body-bold" css={{ color: 'white' }}>
                {stringUtils.capitalize(currentImage.fileName)}
              </Paragraph>
              <Paragraph textStyle="body-sm" css={{ color: 'white' }}>
                {conversionUtils.formatBytes(currentImage.fileSizeBytes)}
              </Paragraph>
            </Box>
          )}

          <Box css={{ position: 'absolute', left: '48%', top: '70px' }}>
            <Text textStyle="body-sm-bold" css={{ color: 'white' }}>
              {currentImageIndex + 1}
            </Text>
            <Text textStyle="body-sm" css={{ color: 'white', paddingLeft: 'tightest', paddingRight: 'tightest' }}>
              /
            </Text>
            <Text textStyle="body-sm" css={{ color: 'white' }}>
              {imageAttachments.length}
            </Text>
          </Box>

          {currentImage.src && (
            <IconButton
              data-eventid={IRENE_PREVIEW_ATTACHMENTS_EVENT_TYPE_ID}
              asChild
              emphasis="bare"
              icon={<IconExternalOutline />}
              onClick={handleEventTracking}
              onImpact={true}
              shape="circle"
              size="md"
              title="Open image in new tab"
              css={{ position: 'absolute', right: 0, marginTop: 'tighter' }}
            >
              <a href={currentImage.src} target="_blank" rel="noopener noreferrer" />
            </IconButton>
          )}
        </>
      )}
    </Box>,
  ];

  const BrandSpinner = () => (
    <Icon>
      <Spinner size="lg" />
    </Icon>
  );

  return (
    <>
      {!!images && (
        <ReactImagesViewer
          backdropCloseable={true}
          currImg={currentImageIndex}
          customControls={customControls}
          imgs={images}
          isOpen={true}
          onClickNext={handleNext}
          onClickPrev={handlePrevious}
          onClose={handleClose}
          showCloseBtn={false}
          showImgCount={false}
          spinner={BrandSpinner}
        />
      )}
    </>
  );
};

export default ImagesViewer;
